import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useRouteMatch, useParams } from "react-router-dom"
import { Table, Skeleton, Popconfirm, notification, Dropdown, Menu, Button, Checkbox } from "antd"
import {
    DownloadOutlined,
    PlusOutlined,
    UploadOutlined,
    FilterOutlined,
    SearchOutlined,
    FileTextOutlined,
    EditOutlined,
    DeleteOutlined,
    DownOutlined,
    FolderOutlined,
    CloseOutlined,
} from "@ant-design/icons"
import moment from "moment"

import {
    fetchPaymentBatches,
    selectPaymentBatches,
    deletePaymentBatch,
    selectTotal,
    selectPagination,
    selectLoading,
    filterPaymentBatches,
    generateVoucherCsv,
    generatePaymentBatchesCsv,
    selectFilters,
    setFilters,
    selectSort,
    setSort,
    setBulkEditBatchIds,
    selectBulkEditBatchIds,
    editBatches,
} from "../paymentBatchesSlice"
import { allObjectValuesEmpty } from "../../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnHeaderTitle from "../../../../components/utilityComponents/table/ColumnHeaderTitle"
import { tableFiltersApplied } from "../../../../functions/objectFunctions/tableFiltersApplied"
import ColumnSearchFilter from "../../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnDateFilter from "../../../../components/utilityComponents/table/ColumnDateFilter"
import useConfirmationModal from "../../../alertsPanel/ShowConfirm"

import Role from "../../../userRoles/Role"
import { formatCurrency } from "../../../../lib/utilities"
import { selectRoles } from "../../../userRoles/userRolesSlice"

export default function PaymentBatchTable({ scopedByClient }) {
    /////*** react router start ***/////
    const { url } = useRouteMatch()
    const { clientId } = useParams()
    /////*** react router end ***/////

    /////*** redux start ***/////
    const dispatch = useDispatch()
    const paymentBatches = useSelector(selectPaymentBatches)
    // console.log(paymentBatches)
    const total = useSelector(selectTotal)
    const pagination = useSelector(selectPagination)
    const loading = useSelector(selectLoading)
    const roles = useSelector(selectRoles)
    const filters = useSelector(selectFilters)
    const sort = useSelector(selectSort)
    const bulkEditBatchIds = useSelector(selectBulkEditBatchIds)
    /////*** redux end ***/////

    /////*** useState start ***/////
    const [loaded, setLoaded] = useState(false)
    // const [filters, setFilters] = useState({})
    // const [sort, setSort] = useState({
    //     column: "created_at",
    //     direction: "desc",
    // })
    /////*** useState end ***/////

    /////*** useEffect start ***/////
    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialPaymentBatches(true)
        }
    }, [loaded, url, dispatch, pagination]) // eslint-disable-line
    /////*** useEffect end ***/////

    /////*** event handler methods start ***/////
    const deleteRecord = async (id) => {
        const response = await dispatch(deletePaymentBatch(id))
        if (response.success) {
            notification.open({
                message: "Success",
                description: "Deleted Vendor Batch",
                duration: 0.8,
            })
        } else {
            notification.open({
                message: "Error",
                description: "There was an error deleting the Vendor Batch",
                duration: 0.8,
            })
        }
    }

    // const handleFetchInitialPaymentBatches = () => {
    //     dispatch(fetchPaymentBatches(clientId, pagination))
    // }
    const handleFetchInitialPaymentBatches = (withFilters = false) => {
        if (!withFilters) {
            dispatch(fetchPaymentBatches(clientId, pagination))
        } else {
            dispatch(filterPaymentBatches({ clientId: clientId, filters: filters, pagination: pagination, sort: sort }))
        }
        // console.log(clientId, pagination, sort, billableAccountId, supplyAccountId, groupAccountId)
    }

    const handleFilterPaymentBatches = (newPagination = null, newSort = null) => {
        return dispatch(
            filterPaymentBatches({
                clientId: clientId,
                filters: filters,
                pagination: !!newPagination ? newPagination : pagination,
                sort: !!newSort ? newSort : sort,
            })
        )
    }

    const handleSortPaymentBatchesChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        dispatch(setSort(newSort))
        handleFilterPaymentBatches(null, newSort)
    }

    const handleTableChange = (newPagination, sorter) => {
        if (allObjectValuesEmpty(filters) && allObjectValuesEmpty(sorter)) {
            dispatch(fetchPaymentBatches(clientId, newPagination))
        } else {
            handleFilterPaymentBatches(newPagination)
        }
    }
    /////*** event handler methods end ***/////

    /////*** render methods start ***/////
    const renderColumnHeaderTitle = (title, columnClass, altTitle) => {
        return <ColumnHeaderTitle title={title} altTitle={altTitle} handleSortChange={handleSortPaymentBatchesChange} sort={sort} columnClass={columnClass} />
    }

    const showConfirm = useConfirmationModal()
    const handleDeleteClick = (id) => {
        showConfirm(deletePaymentBatch, id)
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters) && (allObjectValuesEmpty(sort) || sort.column === "created_at")) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a
                    href="javascript:;"
                    onClick={() => {
                        handleFetchInitialPaymentBatches()
                        dispatch(setFilters({}))
                        dispatch(setSort({ column: "created_at", direction: "desc" }))
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }
    /////*** render methods end ***/////

    const voucherCsv = async (paymentBatchId) => {
        notification.open({
            message: "Vendor Batch Voucher CSV started",
            duration: 0.8,
        })

        dispatch(generateVoucherCsv(paymentBatchId))
    }

    const paymentbatchesCsv = async () => {
        notification.open({
            message: "Vendor batches export started",
            duration: 0.8,
        })

        dispatch(generatePaymentBatchesCsv(clientId, filters))
    }

    function checkBulkEditIds() {
        if (paymentBatches.length === 0 || bulkEditBatchIds.length === 0) {
            return false
        }

        const bulkEditIdSet = new Set(bulkEditBatchIds.map((batch) => batch.id))

        return paymentBatches.every((batch) => bulkEditIdSet.has(batch.id))
    }

    const handleBulkEdit = async () => {
        await dispatch(
            editBatches(bulkEditBatchIds, {
                clientId: clientId,
                filters: filters,
                pagination: pagination,
                sort: sort,
            })
        )
    }

    // const handleChecked = (closed, id) => {
    //     if(!roles.includes("super_admin") && closed){
    //         return true
    //     }
    //     for(let i = 0; i < bulkEditBatchIds.length; i++){
    //         if(bulkEditBatchIds[i].id == id) return true
    //     }
    //     return false
    // }

    const bulkEditBatchIdSet = new Set(bulkEditBatchIds.map((batch) => batch.id))

    const handleChecked = (closed, id) => {
        if (!roles.includes("super_admin") && closed) {
            return true
        }

        return bulkEditBatchIdSet.has(id)
    }

    const countBatches = () => {
        let accsToOpen = 0
        let accsToClose = 0
        bulkEditBatchIds.map((batch, i) => {
            if (batch.closed) accsToClose += 1
            if (!batch.closed) accsToOpen += 1
        })

        return { opening: accsToOpen, closing: accsToClose }
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div>
                            <div className="badged-header">
                                <h2>Vendor Batches</h2>
                                <div className="badge">{total}</div>
                            </div>
                            {bulkEditBatchIds.length > 0 && roles.includes("super_admin") && (
                                <>
                                    <div className="badged-header" style={{ gap: 10, color: "white" }}>
                                        <h5>- Batches to be opened:</h5>
                                        <div className="global-batch-filter-label-text">{countBatches().opening}</div>
                                    </div>
                                    <div className="badged-header" style={{ gap: 10, color: "white" }}>
                                        <h5>- Batches to be closed:</h5>
                                        <div className="global-batch-filter-label-text">{countBatches().closing}</div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="actions">
                            <div className="table-action-links">
                                {bulkEditBatchIds.length > 0 && (
                                    <Role action="create" model="payment_batch">
                                        <a href="javascript:;" onClick={() => handleBulkEdit()}>
                                            <CloseOutlined />
                                            {roles.includes("super_admin") ? "Handle" : "Close"} Batches
                                        </a>
                                    </Role>
                                )}
                                {renderResetFilters()}

                                <Role action="create" model="payment_batch">
                                    <Link to={`${url}/new`}>
                                        <PlusOutlined />
                                        Add New
                                    </Link>
                                </Role>

                                <a href="javascript:;" onClick={() => console.log("Import")}>
                                    <UploadOutlined />
                                    Import
                                </a>

                                <a href="javascript:;" onClick={() => paymentbatchesCsv()}>
                                    <DownloadOutlined />
                                    Export
                                </a>
                            </div>
                        </div>
                    </div>

                    <Skeleton active loading={loading}>
                        <Table dataSource={paymentBatches} pagination={{ ...pagination, showSizeChanger: true, total: total }} onChange={handleTableChange} rowKey={(r) => r.id}>
                            {(roles.includes("super_admin") || roles.includes("muc_admin")) && (
                                <Table.Column
                                    key="in"
                                    dataIndex="in"
                                    render={(text, record, index) => {
                                        // console.log(text, record, index)
                                        return (
                                            <Checkbox
                                                disabled={record.closed && !roles.includes("super_admin")}
                                                checked={handleChecked(record.closed, record.id)}
                                                onClick={(e) => {
                                                    // make into object
                                                    if (e.target.checked) {
                                                        dispatch(setBulkEditBatchIds([...bulkEditBatchIds, { id: record.id, closed: !record.closed }]))
                                                    } else {
                                                        dispatch(setBulkEditBatchIds(bulkEditBatchIds.filter((batch, i) => batch.id !== record.id)))
                                                    }
                                                }}
                                            />
                                        )
                                    }}
                                    title={
                                        <Checkbox
                                            checked={checkBulkEditIds()}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    const bulkEditIdSet = new Set(bulkEditBatchIds.map((batch) => batch.id))
                                                    const newBatches = paymentBatches
                                                        .filter((batch) => {
                                                            if (bulkEditIdSet.has(batch.id)) return false
                                                            if (roles.includes("super_admin")) return true
                                                            return !batch.closed
                                                        })
                                                        .map((batch) => ({ id: batch.id, closed: !batch.closed }))

                                                    dispatch(setBulkEditBatchIds([...bulkEditBatchIds, ...newBatches]))
                                                } else {
                                                    dispatch(setBulkEditBatchIds([]))
                                                }
                                            }}
                                        />
                                    }
                                />
                            )}
                            <Table.Column
                                title={renderColumnHeaderTitle("closed")}
                                key="closed"
                                dataIndex="closed"
                                render={(text, record) => <>{record.closed ? "Closed" : ""}</>}
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialPaymentBatches}
                                        filterAction={filterPaymentBatches}
                                        filterColumn={"batch_date"}
                                        filterType={"batch_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("batch_date_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("payer_name", null, "Payer")}
                                key="payer_name"
                                dataIndex="payer_name"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialPaymentBatches}
                                        filterAction={filterPaymentBatches}
                                        filterColumn={"payer_name"}
                                        filterType={"payer_name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("payer_name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("vendor_names", null, "Vendors")}
                                key="vendor_names"
                                dataIndex="vendor_names"
                                render={(vendorNames) => (Array.isArray(vendorNames) ? vendorNames.join(", ") : vendorNames)}
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialPaymentBatches}
                                        filterAction={filterPaymentBatches}
                                        filterColumn={"vendor_names"}
                                        filterType={"vendor_names_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("vendor_names_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("batch_date")}
                                key="batch_date"
                                dataIndex="batch_date"
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialPaymentBatches}
                                        filterAction={filterPaymentBatches}
                                        filterColumn={"batch_date"}
                                        filterType={"batch_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("batch_date_lteq" || "batch_date_gteq")}
                                render={(text, record, index) => moment(text).format("L")}
                            />
                            {/* TODO: Make money formatting a library function */}
                            <Table.Column
                                title={renderColumnHeaderTitle("bills_due_date")}
                                key="bills_due_date"
                                dataIndex="bills_due_date"
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialPaymentBatches}
                                        filterAction={filterPaymentBatches}
                                        filterColumn={"bills_due_date"}
                                        filterType={"bills_due_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("bills_due_date_lteq" || "bills_due_date_gteq")}
                                render={(text, record, index) => moment(text).format("L")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("batch_total", "right_align_column")}
                                key="batch_total"
                                dataIndex="batch_total"
                                align="right"
                                render={(text, record, index) => formatCurrency(text)}
                            />
                            <Table.Column title="Notes" key="notes" dataIndex="notes" />
                            <Table.Column title="Paid as" key="paid_as" dataIndex="paid_as" />
                            {/* {roles.includes("super_admin") && (
                                <Table.Column
                                    title={renderColumnHeaderTitle("created_at")}
                                    key="created_at"
                                    dataIndex="created_at"
                                    // filterDropdown={({ visible }) => (
                                    //     <ColumnDateFilter
                                    //         handleFetchInitial={handleFetchInitialBills}
                                    //         filterAction={filterBills}
                                    //         filterColumn={"created_at"}
                                    //         filterType={"created_at"}
                                    //         filters={filters}
                                    //         pagination={pagination}
                                    //         setFilters={setFilters}
                                    //         sort={sort}
                                    //         setSort={setSort}
                                    //         visible={visible}
                                    //     />
                                    // )}
                                    // filterIcon={() => renderSearchOutlinedButton("created_at_lteq" || "created_at_gteq")}
                                    render={(text, record, index) => moment(text).format("MM/DD/YY - h:mm:ss A")}
                                />
                            )} */}
                            <Table.Column
                                title=""
                                key="action"
                                render={(text, record) => (
                                    <>
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    <Role action="read" model="payment_batch">
                                                        <Menu.Item key="1" icon={<FolderOutlined />}>
                                                            <Link to={`${url}/${record.id}/vendor_payments`}>Vendor Payments</Link>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Role action="update" model="payment_batch">
                                                        <Menu.Item key="2" icon={<FolderOutlined />}>
                                                            <Link to={`${url}/${record.id}/bills`}>Bills</Link>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Role action="read" model="payment_batch">
                                                        <Menu.Item key="3" icon={<FileTextOutlined />} onClick={() => voucherCsv(record.id)}>
                                                            Voucher CSV
                                                        </Menu.Item>
                                                    </Role>

                                                    <Role action="update" model="payment_batch">
                                                        <Menu.Item key="4" icon={<EditOutlined />}>
                                                            <Link to={`${url}/${record.id}/edit`}>Edit Vendor Batch</Link>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Role action="destroy" model="payment_batch">
                                                        <Menu.Item key="5" icon={<DeleteOutlined />} onClick={() => handleDeleteClick(record.id)} disabled={record.has_been_closed}>
                                                            Delete Vendor Batch
                                                        </Menu.Item>
                                                    </Role>
                                                </Menu>
                                            }>
                                            <Button>
                                                Actions <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    </>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
            {/* <div className="card-footer">
                <div className="card-footer-left"></div>
                <div className="card-footer-right pagination"></div>
            </div> */}
        </div>
    )
}
